import { dateText } from '@/pages/access-admin/tenant/format';
import { clockText, dayOf } from '@/pages/access-admin/tenant/historyFormat';

import type {
  Channel,
  NotificationItem,
  NotificationState,
  NotificationView,
  SubscriptionTopic,
} from './api';

/** The paths of the notification centre (routes/index.tsx). */
export const NOTIFICATIONS_PATH = '/notifications';
export const NOTIFICATION_SETTINGS_PATH = '/notification-settings';

export const VIEW_LABEL: Record<NotificationView, string> = {
  all: 'All',
  action: 'Needs action',
  unread: 'Unread',
};

/**
 * The product areas of the page's filter (the `area` of the API). `live`: a service publishes
 * to it today (identity: access, dbs: data uploads); the others are listed, disabled, as
 * "Coming soon" until their service publishes (owner 2026-10-22).
 */
export const AREA_OPTIONS: readonly { value: string; label: string; live: boolean }[] = [
  { value: 'access', label: 'Access', live: true },
  { value: 'data', label: 'Data', live: true },
  { value: 'contracts', label: 'Contracts', live: false },
  { value: 'activations', label: 'Activations', live: false },
  { value: 'scouting', label: 'Brand scouting', live: false },
  { value: 'reports', label: 'Reports', live: false },
  { value: 'product', label: 'Product updates', live: false },
];

export const areaLabel = (area: unknown): string =>
  AREA_OPTIONS.find((a) => a.value === area)?.label ?? 'Other';

export const CHANNEL_LABEL: Record<Channel, string> = {
  app: 'In the app',
  email: 'E-mail',
  both: 'Both',
  off: 'Off',
};

const CHANNEL_ORDER: Channel[] = ['app', 'email', 'both', 'off'];

/**
 * The choices of a topic in the fixed order: only those the API offers, and never Off for a
 * mandatory subscription.
 */
export const choicesOf = (topic: Pick<SubscriptionTopic, 'choices' | 'mandatory'>): Channel[] =>
  CHANNEL_ORDER.filter(
    (c) =>
      Array.isArray(topic.choices) &&
      topic.choices.includes(c) &&
      !(topic.mandatory === true && c === 'off'),
  );

/** The chip of an item (the mock-up's kinds): its colours and a short name. */
export interface KindChip {
  label: string;
  fg: string;
  bg: string;
}

const AMBER = { fg: '#7a4b00', bg: '#fff4dd' };
const RED = { fg: '#b42318', bg: '#fdecea' };
const BLUE = { fg: '#1565c0', bg: '#e3f0fd' };
const PURPLE = { fg: '#3f3a9e', bg: '#ecebfb' };
const GREEN = { fg: '#1e7a3c', bg: '#e6f4ea' };
const GREY = { fg: '#2b323b', bg: '#eceef1' };

const TOPIC_CHIP: Record<string, KindChip> = {
  'access.security': { label: 'Security', ...RED },
  'access.requests': { label: 'Approval', ...AMBER },
  'product.updates': { label: 'Product update', ...BLUE },
  'approval.waiting': { label: 'Approval', ...AMBER },
  'access.ending': { label: 'Access ending', ...AMBER },
  'access.people': { label: 'People', ...GREEN },
  'access.roles': { label: 'Roles', ...PURPLE },
  'access.organisation_setup': { label: 'Organisation', ...BLUE },
  'access.invitations': { label: 'Invitations', ...GREEN },
  'product.release_notes': { label: 'Product update', ...BLUE },
};

const text = (value: unknown): string => (typeof value === 'string' ? value : '');

/** The accent of each area (the timeline's dot of a plain `info` item). */
export const AREA_ACCENT: Record<string, string> = {
  access: '#3f3a9e',
  contracts: '#0e7490',
  activations: '#9a3412',
  scouting: '#7e22ce',
  reports: '#1565c0',
  data: '#334155',
  product: '#1565c0',
};
const DEFAULT_ACCENT = '#6b7684';

export const areaAccent = (area: unknown): string => AREA_ACCENT[text(area)] ?? DEFAULT_ACCENT;

export function kindOf(item: Pick<NotificationItem, 'topic' | 'area'>): KindChip {
  const topic = text(item.topic);
  if (TOPIC_CHIP[topic]) return TOPIC_CHIP[topic];
  if (topic.startsWith('security.')) return { label: 'Security', ...RED };
  const area = AREA_OPTIONS.find((a) => a.value === text(item.area));
  return { label: area?.label ?? 'Update', ...GREY };
}

/** The tone of a state: its words and colours (`info` has none: the row as before). */
export interface StateTone {
  label: string;
  fg: string;
  bg: string;
}

export const STATE_TONE: Record<Exclude<NotificationState, 'info'>, StateTone> = {
  in_progress: { label: 'In progress', fg: '#1565c0', bg: '#e3f0fd' },
  done: { label: 'Done', fg: '#1e7a3c', bg: '#e6f4ea' },
  failed: { label: 'Failed', fg: '#b42318', bg: '#fdecea' },
};

export const stateToneOf = (item: Pick<NotificationItem, 'state'>): StateTone | null =>
  item.state && item.state !== 'info' ? (STATE_TONE[item.state] ?? null) : null;

/** The progress bar's words: "45%", or "In progress" when the service gives no figure. */
export const progressText = (progress: number | null): string =>
  progress === null ? 'In progress' : `${progress}%`;

/** True when the item was updated in place after it was created. */
export function isUpdated(item: Pick<NotificationItem, 'created_at' | 'updated_at'>): boolean {
  const created = Date.parse(text(item.created_at));
  const updated = Date.parse(text(item.updated_at));
  return Number.isFinite(created) && Number.isFinite(updated) && updated > created;
}

const ymd = (d: Date): string => d.toISOString().slice(0, 10);

export type DayBucket = 'Today' | 'Yesterday' | 'Earlier';

/** Today / Yesterday / Earlier by the UTC day of `created_at` (like the times shown). */
export function bucketOf(createdAt: string, now: Date = new Date()): DayBucket {
  const day = dayOf(text(createdAt));
  if (day === ymd(now)) return 'Today';
  if (day === ymd(new Date(now.getTime() - 86_400_000))) return 'Yesterday';
  return 'Earlier';
}

/** "10:12 UTC" today and yesterday, "16 Oct 2026" before. */
export function whenText(createdAt: string, now: Date = new Date()): string {
  return bucketOf(createdAt, now) === 'Earlier'
    ? dateText(text(createdAt))
    : clockText(text(createdAt));
}

/** The panel's and the page's order: "Needs your action" first, then the days. */
export function groupNotifications(
  items: NotificationItem[],
  now: Date = new Date(),
): { action: NotificationItem[]; days: { label: DayBucket; items: NotificationItem[] }[] } {
  const action = items.filter((i) => i.needs_action === true);
  const rest = items.filter((i) => i.needs_action !== true);
  const days = (['Today', 'Yesterday', 'Earlier'] as DayBucket[])
    .map((label) => ({ label, items: rest.filter((i) => bucketOf(i.created_at, now) === label) }))
    .filter((g) => g.items.length > 0);
  return { action, days };
}

/**
 * The router path of an item's link: an in-app hash route only (`#/settings/approvals`,
 * `/#/settings/approvals` as the publishers write it, or the path without the hash). Anything
 * else (another site, `javascript:`, a protocol-relative URL) gives null: the item is only
 * marked read.
 */
export function inAppPath(href: unknown): string | null {
  const raw = text(href).trim();
  const path = raw.startsWith('#/') ? raw.slice(1) : raw.startsWith('/#/') ? raw.slice(2) : raw;
  if (!path.startsWith('/') || path.startsWith('//') || path.includes('\\')) return null;
  if ([...path].some((ch) => ch.charCodeAt(0) < 32)) return null;
  return path;
}

export const isUnread = (item: Pick<NotificationItem, 'read_at'>): boolean => !item.read_at;

/** The text fields as given: plain text, never markup (React escapes it). */
export const plain = text;

/* ---- The timeline of the Notifications page: the browser's local time. ---- */

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const pad = (n: number): string => String(n).padStart(2, '0');

const localDate = (iso: unknown): Date | null => {
  const t = Date.parse(text(iso));
  return Number.isFinite(t) ? new Date(t) : null;
};

/** The local day of a time, `YYYY-MM-DD` ('' when it is not a time). */
export function localDayKey(iso: unknown): string {
  const d = localDate(iso);
  return d ? `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` : '';
}

/** "09:49" in local time ('' when it is not a time). */
export function localTime(iso: unknown): string {
  const d = localDate(iso);
  return d ? `${pad(d.getHours())}:${pad(d.getMinutes())}` : '';
}

/** "Today", "Yesterday", "Mon 19 Oct" (with the year when it is not this year). */
export function localDayLabel(dayKey: string, now: Date = new Date()): string {
  const [y, m, d] = dayKey.split('-').map(Number);
  if (!y || !m || !d) return 'Earlier';
  const day = new Date(y, m - 1, d);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  if (day.getTime() === today.getTime()) return 'Today';
  if (day.getTime() === yesterday.getTime()) return 'Yesterday';
  const label = `${WEEKDAYS[day.getDay()]} ${d} ${MONTHS[m - 1]}`;
  return y === now.getFullYear() ? label : `${label} ${y}`;
}

/**
 * The page's timeline: "Needs your action" pinned, then the rest by local day, in the order
 * the service sends them (newest first); a day split over two pages stays one day.
 */
export function timelineOf(
  items: NotificationItem[],
  now: Date = new Date(),
): {
  action: NotificationItem[];
  days: { key: string; label: string; items: NotificationItem[] }[];
} {
  const action = items.filter((i) => i.needs_action === true);
  const days: { key: string; label: string; items: NotificationItem[] }[] = [];
  for (const item of items) {
    if (item.needs_action === true) continue;
    const key = localDayKey(item.created_at);
    const day = days.find((g) => g.key === key);
    if (day) day.items.push(item);
    else days.push({ key, label: localDayLabel(key, now), items: [item] });
  }
  return { action, days };
}
